import React from 'react';

const TimingAnimation = ({
  // Position parameters
  startX = 50,
  y = 175,
  arrowY = 35,
  xOffset = 0,         // New parameter for horizontal offset

  // Color parameters
  strokeColor = "#666",
  textColor = "#43a047",
  rectColor = "#43a047",
  arrowColor = "#43a047",

  // Animation timing parameters
  firstArrowStart = "0.5s",
  firstArrowDuration = "2s",
  secondArrowStart = "3s",
  secondArrowDuration = "2s",
  firstEllipseStart = "1s",
  secondEllipseStart = "3.5s",
  lineStart = "4s",
  rectStart = "4.5s",

  // Arrow coordinates
  firstArrowStart1 = 105,
  firstArrowEnd1 = 195,
  secondArrowStart2 = 205,
  secondArrowEnd2 = 295,

  // Text parameters
  averageText = "Average = 1 s = 1000 ms",
  arrowText = "1 s", // Kept for backward compatibility
  firstArrowText = undefined, // New prop for first arrow
  secondArrowText = undefined, // New prop for second arrow
  ellipseText = undefined,
  firstEllipseText = undefined,
  secondEllipseText = undefined,

  // Visibility control
  ellipsesStayVisible = false
}) => {
  // Calculate derived positions with offset
  const baseX = startX + xOffset;  // Apply offset to base position
  const plusX = baseX + 35;
  const secondEllipseX = baseX + 70;
  const lineStartX = baseX - 25;
  const lineEndX = secondEllipseX + 25;
  const rectMargin = 20;
  const rectX = lineStartX - rectMargin;
  const rectWidth = (lineEndX - lineStartX) + (rectMargin * 2);
  const textX = (lineStartX + lineEndX) / 2;

  // Determine which text to use for ellipses
  const firstEllipseDisplayText = firstEllipseText || ellipseText || arrowText;
  const secondEllipseDisplayText = secondEllipseText || ellipseText || arrowText;

  return (
    <>
      {/* First arrow with text */}
      <g opacity="0">
        <animate
          attributeName="opacity"
          values="0; 1; 1; 0"
          keyTimes="0; 0.1; 0.9; 1"
          dur={firstArrowDuration}
          begin={firstArrowStart}
          fill="freeze"
        />
        <line
          x1={firstArrowStart1}
          y1={arrowY}
          x2={firstArrowEnd1}
          y2={arrowY}
          stroke={arrowColor}
          strokeWidth="2"
          markerStart="url(#startArrow)"
          markerEnd="url(#endArrow)"
        />
        <text
          x={(firstArrowStart1 + firstArrowEnd1) / 2}
          y={arrowY - 5}
          textAnchor="middle"
          fill={arrowColor}
          fontSize="12"
          fontFamily="Arial, sans-serif"
        >
          {firstArrowText || arrowText}
        </text>
      </g>

      {/* Second arrow with text */}
      <g opacity="0">
        <animate
          attributeName="opacity"
          values="0; 1; 1; 0"
          keyTimes="0; 0.1; 0.9; 1"
          dur={secondArrowDuration}
          begin={secondArrowStart}
          fill="freeze"
        />
        <line
          x1={secondArrowStart2}
          y1={arrowY}
          x2={secondArrowEnd2}
          y2={arrowY}
          stroke={arrowColor}
          strokeWidth="2"
          markerStart="url(#startArrow)"
          markerEnd="url(#endArrow)"
        />
        <text
          x={(secondArrowStart2 + secondArrowEnd2) / 2}
          y={arrowY - 5}
          textAnchor="middle"
          fill={arrowColor}
          fontSize="12"
          fontFamily="Arial, sans-serif"
        >
          {secondArrowText || arrowText}
        </text>
      </g>

      {/* First ellipse with text */}
      <g opacity={ellipsesStayVisible ? "1" : "0"}>
        <animate
          attributeName="opacity"
          values={ellipsesStayVisible ? "0; 1; 1; 1" : "0; 1; 1; 1"}
          keyTimes="0; 0.1; 0.9; 1"
          dur="2s"
          begin={firstEllipseStart}
          fill="freeze"
        />
        <ellipse
          cx={baseX}
          cy={y}
          rx="20"
          ry="15"
          stroke={strokeColor}
          strokeWidth="2"
          fill="none"
        />
        <text
          x={baseX}
          y={y + 4}
          textAnchor="middle"
          fill={textColor}
          fontSize="12"
          fontFamily="Arial, sans-serif"
        >
          {firstEllipseDisplayText}
        </text>
      </g>

      {/* Plus sign */}
      <g opacity="0">
        <animate
          attributeName="opacity"
          values="0; 1; 1; 1"
          keyTimes="0; 0.1; 0.9; 1"
          dur="1s"
          begin={firstEllipseStart}
          fill="freeze"
        />
        <text
          x={plusX}
          y={y + 5}
          textAnchor="middle"
          fill={strokeColor}
          fontSize="16"
          fontFamily="Arial, sans-serif"
        >
          +
        </text>
      </g>

      {/* Second ellipse with text */}
      <g opacity={ellipsesStayVisible ? "1" : "0"}>
        <animate
          attributeName="opacity"
          values={ellipsesStayVisible ? "0; 1; 1; 1" : "0; 1; 1; 1"}
          keyTimes="0; 0.1; 0.9; 1"
          dur="2s"
          begin={secondEllipseStart}
          fill="freeze"
        />
        <ellipse
          cx={secondEllipseX}
          cy={y}
          rx="20"
          ry="15"
          stroke={strokeColor}
          strokeWidth="2"
          fill="none"
        />
        <text
          x={secondEllipseX}
          y={y + 4}
          textAnchor="middle"
          fill={textColor}
          fontSize="12"
          fontFamily="Arial, sans-serif"
        >
          {secondEllipseDisplayText}
        </text>
      </g>

      {/* Result line */}
      <g opacity="0">
        <animate
          attributeName="opacity"
          values="0; 1; 1; 1"
          keyTimes="0; 0.1; 0.9; 1"
          dur="1s"
          begin={lineStart}
          fill="freeze"
        />
        <line
          x1={lineStartX}
          y1={y + 20}
          x2={lineEndX}
          y2={y + 20}
          stroke={strokeColor}
          strokeWidth="2"
        />
      </g>

      {/* Result rectangle with text */}
      <g opacity="0">
        <animate
          attributeName="opacity"
          values="0; 1; 1; 1"
          keyTimes="0; 0.1; 0.9; 1"
          dur="1s"
          begin={rectStart}
          fill="freeze"
        />
        <rect
          x={rectX}
          y={y + 30}
          width={rectWidth}
          height="25"
          fill={rectColor}
          rx="4"
        />
        <text
          x={textX}
          y={y + 47}
          textAnchor="middle"
          fill="white"
          fontSize="12"
          fontFamily="Arial, sans-serif"
        >
          {averageText}
        </text>
      </g>
    </>
  );
};

export default TimingAnimation;